import { useState } from 'react';
import type { DeclareAnswerState } from '../lib/useJobsInbox';
import styles from './DeclaredInputBanner.module.css';

// =============================================================================
// DeclaredInputBanner — D19/D17, THE DECLARED-INPUTS ASK.
//
// Jason, verbatim (D19): "There must be a thing that says enter your home
// address to find targeted jobs near you, or your postal code. That's all we
// need. But it has to be SURFACED, ANNOUNCED, and then CONTINUALLY
// REINFORCED. Do this, please, before you run your search."
//
// Renders BEFORE the jobs list (Jobs.tsx wires it above JobSearchBar/results)
// exactly when GET /jobs's own served declaredInputs.missingInputs is
// non-empty — never a client-derived guess about what's missing (the server
// already knows, from declared_answers). One small input + a submit button,
// per hard rule 2 (no long forms) — this is the ONE-FIELD case that rule
// explicitly allows. Persistently reinforced: this component has no
// "dismiss forever" affordance — it renders every time the gate is unmet,
// which is the honest behaviour his own words ask for ("continually
// reinforced"), not a nag counter that eventually gives up.
// =============================================================================

export interface DeclaredInputBannerProps {
  missingInputs: string[];
  declareState: DeclareAnswerState;
  onDeclare: (questionKey: string, answer: string) => void;
}

export function DeclaredInputBanner({ missingInputs, declareState, onDeclare }: DeclaredInputBannerProps) {
  const [value, setValue] = useState('');
  if (missingInputs.length === 0) return null;

  const saving = declareState.status === 'saving';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    // A single input covers BOTH declarable geography keys (his own wording:
    // "enter your home address... or your postal code. That's all we need.")
    // — a bare 5-digit US ZIP reads as a postal code, anything else is read
    // as the honest free-text home-country/location answer. Never a second
    // field asking which kind this is (hard rule 2: no long forms).
    const questionKey = /^\d{4,10}$/.test(trimmed) ? 'home_postal_code' : 'home_country';
    onDeclare(questionKey, trimmed);
  };

  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <div className={styles.bannerTitle}>Enter your postal code to find targeted jobs near you</div>
      <div className={styles.bannerBody}>
        We don't know where "near you" means yet — every job's home/relocate/bridge read depends on
        it. One field, that's all we need.
      </div>
      <form className={styles.bannerForm} onSubmit={handleSubmit}>
        <input
          type="text"
          className={styles.bannerInput}
          placeholder="Postal code or home country"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          aria-label="Postal code or home country"
        />
        <button type="submit" className={styles.bannerSubmit} disabled={saving || !value.trim()}>
          {saving ? 'Saving…' : 'Set it'}
        </button>
      </form>
      {declareState.status === 'error' ? (
        <div className={styles.bannerError} role="alert">
          {declareState.error}
        </div>
      ) : null}
    </div>
  );
}
